import { Pressable } from '@react-aria/interactions'
import { Lightning } from 'blaze-ui/icons'

import Styles from './VirtualMarketerUpgradeButton.module.scss'

import { useModals } from 'almanac-ui/Modal'
import { useVirtualMarketerPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/hooks/useVirtualMarketerPlan'
import { useRootStore } from 'stores/RootStore'

import { Text } from 'blaze-ui/Text/Text'
import { pluralize } from 'utils'
import { VirtualMarketerPlansModal } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/VirtualMarketerPlansModal/VirtualMarketerPlansModal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { observer } from 'mobx-react-lite'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useCampaignsFeatureFlag } from 'hooks/useCampaignsFeatureFlag'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { useIsActiveOrCanceledPioneerPlan } from 'hooks/useIsActiveOrCanceledPioneerPlan'
import { Button } from 'blaze-ui/Button'
import LockOpen3 from 'blaze-ui/icons/24/LockOpen3'
import { useIsSmallDesktopScreen } from 'hooks/useIsSmallDesktopScreen'

export const VirtualMarketerUpgradeButton = observer(
  ({ showTrialDaysLeft = true }: { showTrialDaysLeft?: boolean }) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const isCampaignsEnabled = useCampaignsFeatureFlag()
    const { openModal, closeAllModals } = useModals()
    const tierUsageStore = useRootStore().tierUsageStore
    const { currentSubscription } = tierUsageStore
    const { data: virtualMarketerPlan } = useVirtualMarketerPlan()
    const workspace = useCurrentWorkspace()
    const isActiveOrCanceledPioneerPlan = useIsActiveOrCanceledPioneerPlan()
    const isSmallDesktopScreen = useIsSmallDesktopScreen()

    const openVirtualMarketPlansModal = () => {
      if (isCampaignsEnabled || isActiveOrCanceledPioneerPlan) {
        openModal(AutopilotPlansModal)
        return
      }

      if (!virtualMarketerPlan) return

      openModal(VirtualMarketerPlansModal, {
        plan: virtualMarketerPlan,
        onSuccess: () => {
          if (workspace) tierUsageStore.refetch(workspace.id)

          closeAllModals()
        },
      })
    }

    if (
      !workspace?.is_virtual_marketer ||
      !virtualMarketerPlan ||
      !currentSubscription ||
      currentSubscription.subscription_payment_method
    ) {
      return null
    }

    const trialDaysLeft = currentSubscription?.trial_days_left || 0

    if (isSystemRedesignEnabled) {
      return (
        <div className={Styles.container}>
          {showTrialDaysLeft && trialDaysLeft > 0 && (
            <div className={Styles.trialDaysLeft}>
              <Text variant="secondary" fontWeight="500" color="var(--upgrade)">
                {trialDaysLeft} {pluralize('day', trialDaysLeft)}
              </Text>
              <Text variant="secondary" fontWeight="400" color="var(--upgrade)">
                {isSmallDesktopScreen ? 'left' : 'left in your trial'}
              </Text>
            </div>
          )}
          <Button
            onPress={openVirtualMarketPlansModal}
            frontIcon={LockOpen3}
            color="var(--upgrade)"
            variant="secondary"
          >
            Upgrade
          </Button>
        </div>
      )
    }

    return (
      <div className={Styles.container}>
        {showTrialDaysLeft && trialDaysLeft > 0 && (
          <div className={Styles.trialDaysLeft}>
            <Text variant="secondary" fontWeight="700" color="var(--purple-70)">
              {trialDaysLeft} {pluralize('day', trialDaysLeft)}
            </Text>
            <Text variant="secondary" fontWeight="400" color="var(--purple-70)">
              left in your trial
            </Text>
          </div>
        )}
        <Pressable onPress={() => openVirtualMarketPlansModal()}>
          <div className={Styles.button}>
            <Lightning size={isSystemRedesignEnabled ? 16 : undefined} color="var(--purple-70)" />
            <div className={Styles.textContainer}>
              <span>Upgrade</span>
            </div>
          </div>
        </Pressable>
      </div>
    )
  },
)
